import checkDisplayText from 'shared/verifications/checks_display_text.json';

import { CheckAttributeMatch, CheckCategory, CheckStatus } from 'dashboard/models/check';
import {
  CHECK_PROPERTIES,
  CheckProperties,
  ComparedAttribute,
} from 'dashboard/models/verification-check';
import { ReasonConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { CheckName, CheckType } from 'lib/verificationCheck';

export const CHECK_STATUS_DISPLAY = {
  [CheckStatus.Passed]: 'Passed',
  [CheckStatus.Failed]: 'Failed',
  [CheckStatus.NotApplicable]: 'Not applicable',
};

export const CHECK_CATEGORY_DISPLAY: Record<CheckCategory, string> = {
  [CheckCategory.Fraud]: 'Fraud',
  [CheckCategory.UserActionRequired]: 'User action required',
  [CheckCategory.Validity]: 'Validity',
  [CheckCategory.Biometrics]: 'Biometrics',
};

export type CheckDisplayConfig = {
  name: string;
  category: CheckCategory;
  description: string;
  reasons: {
    [name: string]: string;
  };
  type: CheckType;
  showOnlyOnFail?: boolean;
  alternativeName?: string;
};

export const CHECK_ATTRIBUTE_MATCH_DISPLAY: Record<CheckAttributeMatch, string> = {
  [CheckAttributeMatch.Full]: 'Full',
  [CheckAttributeMatch.Partial]: 'Partial',
  [CheckAttributeMatch.None]: 'None',
  [CheckAttributeMatch.Missing]:
    'No record found of an individual associated with all the provided properties',
};

export const CHECK_DISPLAY_CONFIG: Record<CheckName, CheckDisplayConfig> =
  checkDisplayText as Record<CheckName, CheckDisplayConfig>;

type CheckDefaultStatusToReasonsMap = {
  // NOTE: customers can override this using go/reason-configs.
  // TODO: fix this type to match capitalizations of CheckStatus later
  Passed?: string[];
  Failed?: string[];
  NotApplicable?: string[];
};

function generateCheckReasonMapping(checkProperties: CheckProperties) {
  return Object.values(CheckName).reduce<
    Partial<Record<CheckName, CheckDefaultStatusToReasonsMap>>
  >((mapping, checkName) => {
    const reasonsAsArray = Object.entries(checkProperties[checkName].reasons);
    const statusToReasonsMap: CheckDefaultStatusToReasonsMap = {
      Passed: reasonsAsArray
        .filter(
          ([, reasonMetadata]) =>
            reasonMetadata.default_status === 'passed' && reasonMetadata.frontend_visibility,
        )
        .map(([reason]) => reason),
      Failed: reasonsAsArray
        .filter(
          ([, reasonMetadata]) =>
            reasonMetadata.default_status === 'failed' && reasonMetadata.frontend_visibility,
        )
        .map(([reason]) => reason),
      NotApplicable: reasonsAsArray
        .filter(
          ([, reasonMetadata]) =>
            reasonMetadata.default_status === 'not_applicable' &&
            reasonMetadata.frontend_visibility,
        )
        .map(([reason]) => reason),
    };
    return {
      ...mapping,
      [checkName]: statusToReasonsMap,
    };
  }, {}) as Record<CheckName, CheckDefaultStatusToReasonsMap>;
}

// Dynamically generate the mapping from frontend CheckName to Check reason namespaces.
export const CHECK_REASON_MAPPING: Record<CheckName, CheckDefaultStatusToReasonsMap> =
  generateCheckReasonMapping(CHECK_PROPERTIES);

function generateDefaultCheckReasonConfigsMappings(): Record<CheckName, ReasonConfig[]> {
  return Object.entries(CHECK_PROPERTIES).reduce(
    (mapping, [checkName, checkData]) => {
      const reasons = checkData.reasons;
      mapping[checkName as CheckName] = Object.entries(reasons).map(([reason, config]) => ({
        reason,
        // TODO: fix this. we're coercing CheckReasonStatus into CheckStatus but that shouldn't be
        // necessary because they are 2 distinct different statuses.
        status: (Object.values(CheckStatus).includes(
          config.default_status as unknown as CheckStatus,
        )
          ? config.default_status
          : CheckStatus.NotApplicable) as CheckStatus,
      }));
      return mapping;
    },
    {} as Record<CheckName, ReasonConfig[]>,
  );
}

export const DEFAULT_CHECK_REASON_CONFIGS: Record<CheckName, ReasonConfig[]> =
  generateDefaultCheckReasonConfigsMappings();

export const ID_VERSION_MAP: Record<string, string> = {
  '01': '2000',
  '02': '2003',
  '03': '2005',
  '04': '2009',
  '05': '2010',
  '06': '2011',
  '07': '2012',
  '08': '2013',
  '09': '2016',
  '10': '2020',
};

// Colors code retrieved from https://www.aamva.org/D20/
export const ID_COLOR_MAP: Record<string, string> = {
  // eyes
  BLK: 'Black',
  BLU: 'Blue',
  BRO: 'Brown',
  DIC: 'Dichromatic',
  GRY: 'Grey',
  GRN: 'Green',
  HAZ: 'Hazel',
  MAR: 'Maroon',
  PNK: 'Pink',
  UNK: 'Unknown',
  // hair
  BAL: 'Bald',
  BLN: 'Blond',
  RED: 'Red/Auburn',
  SDY: 'Sandy',
  WHI: 'White',
};

// display strings for configuring check-requirements in FormVerificationTemplateChecks
export const COMPARED_ATTRIBUTES_DISPLAY: Record<keyof typeof ComparedAttribute, string> = {
  NameFirst: 'First Name',
  NameMiddle: 'Middle Name',
  NameLast: 'Last Name',
  AddressStreet1: 'Street 1',
  AddressStreet2: 'Street 2',
  AddressCity: 'City',
  AddressSubdivision: 'Subdivision',
  AddressPostalCode: 'Postal Code',
  AddressCountryCode: 'Country Code',
  Birthdate: 'Birthdate',
  IdentificationNumber: 'Identification Number',
  IdentificationClass: 'Identification Class',
  PhoneNumber: 'Phone Number',
  EmailAddress: 'Email Address',
  SocialSecurityNumber: 'Social Security Number',
  Eyes: 'Eye Color',
  Sex: 'Sex',
  Height: 'Height',
  Weight: 'Weight',
  IssuingCountry: 'Issuing Country',
  IssueDate: 'Issue Date',
  ExpirationDate: 'Expiration Date',
};
